'use client'

/**
 * PDP PRODUCT_SUMMARY widget — intentionally renders NOTHING (parity with non-PL).
 *
 * This widget used to render the product description HTML + the value-prop icon strip ("Expert
 * Craftsmanship", "Always Vermeil", …) via the shared `ProductDescription` atom with
 * `showNewPdpDesignV2: true`. But that duplicated content the buy box already shows: the PL
 * PRODUCT_PURCHASE → ProductFormWrapper → ProductForm renders the same product `description`
 * (ProductForm's `showProductDescription`), exactly as the non-PL PDP does — non-PL has NO separate
 * summary section.
 *
 * Rendering this widget on top of the buy box caused two bugs:
 *   • STRFRNT-15536 — the product summary showed TWICE (buy box + this widget).
 *   • STRFRNT-15584 — the icon/feature-badge strip appeared only in PL. That strip renders solely
 *     under `ProductDescription`'s `showNewPdpDesignV2`, and only THIS widget passed it; the buy box
 *     renders the description without it, so non-PL never shows the strip.
 *
 * Aligning PL down to non-PL: the description stays in the buy box alone and PRODUCT_SUMMARY renders
 * nothing. Kept registered (not unregistered) so the BE-sent shell is skipped cleanly via `isEmpty`
 * rather than leaving an empty placeholder in the PRODUCT_INFO column.
 *
 * IMPORTANT — the proto still carries value-prop icons (`productSummaryData.icons`) and they DO
 * render (non-PL parity): the buy box reads this widget's icons cross-widget via
 * `useWidgetOfType(WidgetType.PRODUCT_SUMMARY)` and threads them into `product.productDetailIcons` —
 * PRODUCT_PURCHASE (V1) through `protoToProduct`, PRODUCT_PURCHASE_V2 (SVD) through
 * `productPurchaseV2ToSvdWire`'s ctx. The buy-box `<ProductDescription>` then draws them inline via
 * non-PL's existing `getProductDetailsIcons(product)` pipeline — same DOM as non-PL, without
 * reviving this widget. Removing this null-render would re-introduce STRFRNT-15536 / 15584.
 */

import { WidgetType } from '@lastbrand/storefront-proto-registry/api/v1/enums'

import { PLBaseErrorBoundary } from '@src/components/pl-base-error-boundary'
import { PLTypedWidgetInterface } from '@src/components/pl-base-widget'

import type { ProductSummaryWidgetData } from '@lastbrand/storefront-proto-registry/api/v1/widget'

export const ProductSummaryWidget: PLTypedWidgetInterface<ProductSummaryWidgetData, WidgetType.PRODUCT_SUMMARY> = {
  id: WidgetType.PRODUCT_SUMMARY,
  errorBoundary: PLBaseErrorBoundary,
  // Description + icons are owned by the buy box (non-PL parity) — render nothing here.
  render: () => null,
  // Always empty → the renderer skips the shell entirely (no placeholder in the PRODUCT_INFO column).
  isEmpty: () => true,
}
